import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  PivotModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { forwardRef, useMemo } from "react";

import { ChevronRightOutlined, KeyboardArrowDown } from "@mui/icons-material";
import { green, orange, red } from "@mui/material/colors";
import { isNil } from "lodash";
import { useDynamicColumnDefs } from "../../../../_src_shared/utils/agGrid/hooks/useDynamicColumnDefs";
import AgGridClickableTable from "../../../../components/agGrid/tables/AgGridClickableTable";
import BorrowerAssociatedCommentsBadge from "../../../../components/common/Loan/BorrowerAssociatedCommentsBadge";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import SelectedAssociations from "../../../../components/associations/components/display/SelectedAssociations";
import { useSelector } from "react-redux";

ModuleRegistry.registerModules([
  SideBarModule,
  RowGroupingPanelModule,
  MultiFilterModule,
  RowGroupingModule,
  PivotModule,
  TreeDataModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const LoanBorrowersTable = forwardRef(
  (
    {
      rowData,
      onRowClicked,
      simpleTable,
      selectedId,
      masterDetail,
      quickFilterText,
    },
    ref
  ) => {
    const loan = useSelector((state) => state.loanDrilldown);
    const columnDefs = useMemo(() => {
      return [
        {
          headerName: "Name",
          field: "firstName",
          flex: 1,
          cellClass: null,
          cellStyle: {
            paddingTop: "8px",
            paddingBottom: "8px",
            // paddingLeft: "8px",
            paddingRight: "24px",
          },
          pinned: "left",
          minWidth: 200,
          flexShrink: 0,
          autoHeight: true,
          cellRenderer: (params) => {
            return (
              <Flx column>
                <Flx ac gap={1}>
                  {params?.data?._id === selectedId ? (
                    <KeyboardArrowDown className="thin" />
                  ) : (
                    <ChevronRightOutlined className="thin" />
                  )}
                  <span>
                    {params.data?.firstName} {params.data?.lastName}
                  </span>
                  <BorrowerAssociatedCommentsBadge
                    borrowerId={params.data?._id}
                  />
                </Flx>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: "24px" }}
                >
                  {" "}
                  {/* ← Add this */}
                  <SelectedAssociations
                    loan={loan}
                    associations={params?.data?.associations}
                    disableDelete
                  />
                </div>
              </Flx>
            );
          },
        },
        // {
        //   field: "associations",
        //   flex: 1,
        //   cellClass: null,
        //   cellStyle: {
        //     paddingTop: "8px",
        //     paddingBottom: "8px",
        //     paddingRight: "24px",
        //   },
        //   pinned: "left",
        //   autoHeight: true,
        //   cellRenderer: (params) => {
        //     return (
        //       <div onClick={(e) => e.stopPropagation()}>
        //         {" "}
        //         {/* ← Add this */}
        //         <SelectedAssociations
        //           loan={loan}
        //           associations={params?.value}
        //           disableDelete
        //         />
        //       </div>
        //     );
        //   },
        // },

        {
          field: "estimatedFICO",
          headerName: "Estimated FICO",
          type: "number",

          hide: true,
        },
        {
          field: "ficoMid",
          headerName: "FICO Mid",
          type: "number",
        },

        {
          field: "foreignNationalFlag",
          headerName: "Foreign National",
          type: "boolean",
        },
        {
          field: "verifiedLiquidity",
          headerName: "Verified Liquidity",
          type: "dollar",
        },
        {
          field: "verifiedExperienceTotal",
          headerName: "Verified Experience",
          type: "number",
        },
        {
          field: "citizenshipStatus",
          headerName: "Citizenship Status",
        },
        {
          field: "idExpirationDate",
          headerName: "ID Expiration Date",
          type: "date",
        },
        {
          field: "isGuarantor",
          headerName: "Guarantor",
          type: "boolean",
        },
        {
          field: "entityPercentOwnership",
          headerName: "Ownership %",
          type: "percent",
        },
        {
          field: "creditReportDate",
          headerName: "Credit Report Date",
          type: "date",
        },
        {
          field: "primaryAddress",
          headerName: "Primary Address",

          flex: 2,
          minWidth: 200,
          valueFormatter: (params) => {
            return params.value?.fullAddress || "";
          },
        },
        // ------------------------
        {
          field: "isExperienced",
          headerName: "Experienced",
          type: "boolean",
        },
        {
          field: "isPrimaryContactForLoan",
          headerName: "Primary Contact",
          type: "boolean",
        },

        {
          field: "cashLiquidity",
          headerName: "Cash Liquidity",
          type: "dollar",
        },
        {
          field: "otherLiquidity",
          headerName: "Other Liquidity",
          type: "dollar",
        },

        {
          field: "estimatedNumericFICO",
          headerName: "Estimated Numeric FICO",
          type: "number",
        },
        {
          field: "maritalStatus",
          headerName: "Marital Status",
        },

        // {
        //   field: "firstTimeHomebuyer",
        //   headerName: "First Time Homebuyer",
        //   type: "boolean",
        // },
        {
          field: "hasExperienceWithIncomeProducingProperties",
          headerName: "Income Property Experience",
          type: "boolean",
        },
        {
          field: "hasExperienceWithFixFlips",
          headerName: "Fix & Flip Experience",
          type: "boolean",
        },
        {
          field: "hasExperienceWithGroundUpConstruction",
          headerName: "Ground Up Construction Experience",
          type: "boolean",
        },
        {
          field: "housing_events",
          headerName: "Housing Events",
          type: "boolean",
        },
        {
          field: "mortgage_lates",
          headerName: "Mortgage Lates",
        },
        {
          field: "time_since_housing_event",
          headerName: "Time Since Housing Event",
        },
        {
          field: "time_since_bankruptcy",
          headerName: "Time Since Bankruptcy",
        },
        {
          field: "dateOfBirth",
          headerName: "Date of Birth",
          type: "date",
        },

        // {
        //   field: "borrowerSSN",
        //   headerName: "SSN",
        // },
        // {
        //   field: "borrowerIdType",
        //   headerName: "ID Type",
        // },

        {
          field: "creditAuthDate",
          headerName: "Credit Auth Date",
          type: "date",
        },
        {
          field: "backgroundDate",
          headerName: "Background Date",
          type: "date",
        },
        {
          field: "additional_borrower_notes",
          headerName: "Additional Notes",
        },
        {
          field: "integration_id",
          headerName: "Integration ID",
        },
        {
          field: "last_integration_sync",
          headerName: "Last Integration Sync",
          type: "date",
        },
        {
          field: "ficoLow",
          headerName: "FICO Low",
          type: "number",
        },
        {
          field: "ficoMax",
          headerName: "FICO Max",
          type: "number",
        },
        {
          field: "ficoScore",
          headerName: "FICO Score",
          width: 250,
          cellRenderer: CellRendererBorrowerFico,
        },
      ];
    }, [selectedId, loan]);
    const dynamicColumnDefs = useDynamicColumnDefs(rowData, columnDefs, {
      alwaysShowFields: ["ficoScore"],
    });

    return (
      <>
        <AgGridClickableTable
          ref={ref}
          rowData={rowData}
          simpleTable={simpleTable}
          columnDefs={dynamicColumnDefs}
          quickFilterText={quickFilterText}
          autoSize
          selectedId={selectedId}
          path="borrowers"
          onRowClicked={onRowClicked}
        />

        {masterDetail}
      </>
    );
  }
);

const CellRendererBorrowerFico = (params) => {
  const value = params.value;

  if (isNil(value)) return null;

  const { ficoLow, ficoMid, ficoMax, estimatedFICO } = params.data;
  return (
    <Flx column gap={0.2} center>
      <Txt bold sx={{ fontSize: "11px" }}>
        {estimatedFICO}
      </Txt>
      <Flx gap={0.5} center>
        <ScoreItem tooltip="Low" backgroundColor={red[50]} color={red[900]}>
          {ficoLow}
        </ScoreItem>
        <ScoreItem
          tooltip="Mid"
          backgroundColor={orange[50]}
          color={orange[900]}
        >
          {ficoMid}
        </ScoreItem>
        <ScoreItem
          tooltip="High"
          backgroundColor={green[50]}
          color={green[900]}
        >
          {ficoMax}
        </ScoreItem>
      </Flx>
      {/* <LinearProgress value={40} /> */}
    </Flx>
  );
};

const ScoreItem = ({ children, backgroundColor, color, tooltip }) => {
  const styles = useMemo(() => {
    return {
      backgroundColor,
      gap: 0.3,
      borderRadius: "4px",
      px: 0.4,
      py: 0.1,
    };
  }, [backgroundColor]);

  const txtStyles = useMemo(() => {
    return {
      lineHeight: 1,
      color,
    };
  }, [color]);
  return (
    <Flx sx={styles} center>
      <Txt sx={{ ...txtStyles, fontSize: "10px", fontWeight: 400 }}>
        {tooltip}
      </Txt>
      <Txt sx={{ ...txtStyles, fontSize: "10px", fontWeight: 600 }}>
        {children}
      </Txt>
    </Flx>
  );
};

export default LoanBorrowersTable;
